// =====================================================================
// Génération PDF commune aux 3 types de course : liste de départ,
// résultats de manche, résultats d'épreuve (avec ou sans points),
// détail du calcul de pénalité. Repris et généralisé depuis
// TimingSwissSkiExport.jsx (design inspiré VOLA/SkiAlp Pro).
// =====================================================================
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { disciplineLabel, RACE_TYPES } from "./raceTypes";
import { groupByCategoryOrdered } from "../../utils/categories";
import { groupBySections, toSections } from "../../utils/printSort";
import { brandHeader, brandFooter, brandBottomMargin } from "../../utils/pdfBranding";

const PAGE_W = 210;
const MARGIN = 14;
const HEAD_STYLE = { fillColor: [20, 20, 20], textColor: 255 };
const SUBHEAD_STYLE = { fillColor: [230, 230, 230], textColor: 0 };

// Tableaux de listes (départ / résultats) — lisibilité, 04.11.2026 :
// texte NOIR (au lieu du gris par défaut), police plus grande, rangées
// alternées très claires, fin trait entre les lignes ; Rang, Nom et Temps
// en gras ; Club en plus petit ; colonnes de manche télémark sur deux
// lignes (temps en gras, puis « ski (pénalité) » en petit gris).
const BOLD_COLS = ["Rank", "Order", "Name", "Time", "Total"];
function listTableOpts(head) {
    const idx = (name) => head.indexOf(name);
    const columnStyles = {};
    BOLD_COLS.forEach((n) => idx(n) >= 0 && (columnStyles[idx(n)] = { fontStyle: "bold" }));
    // Largeurs fixes (mm) des petites colonnes : plus de retour à la ligne
    // au milieu d'un nombre ; le Nom prend la place restante.
    const W = { Rank: 10, Order: 11, Bib: 9, "FIS Code": 16, YB: 10, NSA: 11, Cat: 14, "Cat.": 14, "Ski Time": 16, Penalty: 14, Time: 16, Total: 16, Gap: 15, "Diff.": 15, Points: 16 };
    head.forEach((h, i) => {
        if (W[h]) columnStyles[i] = { ...(columnStyles[i] || {}), cellWidth: W[h] };
        if (/^Run \d/.test(h)) columnStyles[i] = { cellWidth: 20, halign: "right" };
    });
    if (idx("Name") >= 0) columnStyles[idx("Name")] = { ...columnStyles[idx("Name")], cellWidth: "auto" };
    if (idx("Club") >= 0) columnStyles[idx("Club")] = { fontSize: 7.2, textColor: 40, cellWidth: 32 };
    ["Time", "Total", "Gap", "Diff.", "Points", "Ski Time", "Penalty"].forEach((n) => {
        if (idx(n) >= 0) columnStyles[idx(n)] = { ...(columnStyles[idx(n)] || {}), halign: "right" };
    });
    return {
        theme: "plain",
        styles: { fontSize: 8.5, textColor: 0, cellPadding: { top: 1.7, bottom: 1.7, left: 1.3, right: 1.3 }, valign: "middle", overflow: "linebreak" },
        headStyles: { ...HEAD_STYLE, fontSize: 7.5, fontStyle: "bold", valign: "middle" },
        alternateRowStyles: { fillColor: [242, 244, 247] },
        columnStyles,
        // Cellule {content, detail} : 2e ligne (détail) dessinée en petit gris.
        didParseCell: (data) => {
            // En-têtes alignés comme leur colonne (nombres à droite).
            if (data.section === "head" && columnStyles[data.column.index]?.halign) {
                data.cell.styles.halign = columnStyles[data.column.index].halign;
            }
            const raw = data.cell.raw;
            if (data.section === "body" && raw && typeof raw === "object" && raw.detail) {
                data.cell.styles.valign = "top";
                data.cell.styles.minCellHeight = 10;
                data.cell.styles.fontStyle = "bold";
            }
        },
        didDrawCell: (data) => {
            const raw = data.cell.raw;
            if (data.section !== "body" || !raw || typeof raw !== "object" || !raw.detail) return;
            const doc = data.doc;
            doc.setFontSize(6.8);
            doc.setFont(undefined, "normal");
            doc.setTextColor(85);
            doc.text(String(raw.detail), data.cell.x + data.cell.width - 1.3, data.cell.y + data.cell.height - 1.6, { align: "right" });
            doc.setTextColor(0);
        },
    };
}

// ---------- formats ----------------------------------------------------
// Temps TRONQUÉS au 1/100 (règle FIS), jamais arrondis.
export function fmtTime(ms) {
    if (ms == null) return "";
    const c = Math.floor(Math.abs(ms) / 10);
    const min = Math.floor(c / 6000);
    const sec = Math.floor((c % 6000) / 100);
    const cs = String(c % 100).padStart(2, "0");
    const sign = ms < 0 ? "-" : "";
    return min > 0 ? `${sign}${min}:${String(sec).padStart(2, "0")}.${cs}` : `${sign}${sec}.${cs}`;
}
export const fmtPts = (x) => (x == null ? "" : Number(x).toFixed(2));
const displayCodex = (c) => (c && /^\d{1,4}$/.test(c) ? c.padStart(4, "0") : c || "");

// ---------- en-tête / pied de page -------------------------------------

// Libellés ANGLAIS des PDF (08.10.2026) — documents officiels.
const DISCIPLINE_EN = {
    SL: "Slalom", GS: "Giant Slalom", SG: "Super-G", DH: "Downhill", AC: "Alpine Combined", SC: "Super Combined",
    CR: "Combi Race", PS: "Parallel Slalom", PSL: "Parallel Slalom", PGS: "Parallel Giant Slalom",
    CL: "Classic", SP: "Sprint", PSP: "Parallel Sprint", PSQ: "Parallel Sprint Qualification",
};
const GENDER_EN = { M: "Men", W: "Women", L: "Women", A: "Mixed" };

function header(doc, config, eventKey, subtitle) {
    brandHeader(doc, config); // logos haut gauche / droite (design commun)

    // En-tête en gras, plus grand (08.10.2026).
    doc.setFont(undefined, "bold");
    // Titre réduit si trop long pour passer entre les deux logos.
    const title = config.raceTitle || config.event || eventKey;
    let fs = 16;
    doc.setFontSize(fs);
    while (fs > 11 && doc.getTextWidth(title) > PAGE_W - 2 * (MARGIN + 36)) doc.setFontSize((fs -= 0.5));
    doc.text(title, PAGE_W / 2, 15, { align: "center" });
    doc.setFontSize(13);
    const disc = DISCIPLINE_EN[String(config.discipline || "").toUpperCase()] || disciplineLabel(config);
    const gender = GENDER_EN[config.gender] || "";
    doc.text([disc, gender].filter(Boolean).join(" — "), PAGE_W / 2, 22, { align: "center" });
    doc.setFontSize(12);
    doc.text(subtitle, PAGE_W / 2, 29, { align: "center" });

    doc.setFontSize(9);
    const typeLabel = RACE_TYPES.find((t) => t.value === config.raceType)?.label || "";
    const left = [config.place, config.date].filter(Boolean).join(", ");
    const right = config.codex ? `${typeLabel} Codex ${displayCodex(config.codex)}` : typeLabel;
    doc.text(left, MARGIN, 35);
    doc.text(right, PAGE_W - MARGIN, 35, { align: "right" });
    doc.setFont(undefined, "normal");
    if (config.subTitle) doc.text(config.subTitle, PAGE_W / 2, 35, { align: "center" });
    doc.setDrawColor(0);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, 37, PAGE_W - MARGIN, 37);
    doc.setLineWidth(0.2);
    return 43;
}

function finalize(doc, config) {
    // Pied de page commun : bandeau, Timing / logo 321-timing / Data
    // processing, date et heure de création, numéros de page.
    brandFooter(doc, config);
}

// Tableaux jury/météo + parcours (1 ou 2 manches côte à côte).
function raceSheet(doc, y, config, runIndexes) {
    const half = { left: { left: MARGIN, right: PAGE_W / 2 + 2 }, right: { left: PAGE_W / 2 + 2, right: MARGIN } };
    // Titres de bloc en gras (plus grands), libellés en gras.
    const plain = {
        theme: "plain",
        styles: { fontSize: 8, cellPadding: 1, textColor: 0 },
        headStyles: { fontStyle: "bold", fontSize: 9.5 },
        columnStyles: { 0: { fontStyle: "bold" } },
    };
    const j = config.jury || {};
    const p = (name, nat) => (name ? `${name}${nat ? ` (${nat})` : ""}` : "—");
    const run0 = config.runs?.[runIndexes[0]] || {};

    const juryBody = config.raceType === "populaire"
        ? [["CHIEF OF RACE", p(j.chief, j.chiefNation)]]
        : [
            ["TECHNICAL DELEGATE", p(j.td, j.tdNation)],
            ["REFEREE", p(j.referee, j.refereeNation)],
            ["ASSISTANT REFEREE", p(j.assistant, j.assistantNation)],
            ["CHIEF OF RACE", p(j.chief, j.chiefNation)],
        ];
    autoTable(doc, { ...plain, startY: y, margin: half.left, head: [["JURY", ""]], body: juryBody });
    const y1 = doc.lastAutoTable.finalY;
    autoTable(doc, {
        ...plain, startY: y, margin: half.right, head: [["CONDITIONS", ""]],
        body: [
            ["WEATHER", run0.weather || "—"],
            ["SNOW", run0.snow || "—"],
            ["TEMP. START / FINISH", [run0.tempStart, run0.tempFinish].filter(Boolean).join(" / ") || "—"],
        ],
    });
    y = Math.max(y1, doc.lastAutoTable.finalY) + 3;

    const t = config.technical || {};
    const courseBody = (r = {}) => {
        const fr = (r.forerunners || []).filter((f) => f.name).map((f) => p(f.name, f.nation)).join("\n");
        const rows = [
            ["COURSE SETTER", p(r.courseSetter, r.courseSetterNation)],
            ["COURSE", t.courseName || "—"],
            ["START / FINISH", [t.startAltitude, t.finishAltitude].filter(Boolean).map((a) => `${a} m`).join(" / ") || "—"],
            ["VERTICAL DROP", t.verticalDrop ? `${t.verticalDrop} m` : "—"],
            ["LENGTH", t.length ? `${t.length} m` : "—"],
            ["GATES / TURNING GATES", [r.gates, r.turningGates].filter(Boolean).join(" / ") || "—"],
            ["START TIME", r.startTime || "—"],
        ];
        if (config.raceType !== "populaire") {
            rows.splice(2, 0, ["HOMOLOGATION", t.homologation || "—"]);
            rows.push(["FORERUNNERS", fr || "—"]);
        }
        return rows;
    };
    if (runIndexes.length === 1) {
        autoTable(doc, { ...plain, startY: y, margin: { left: MARGIN, right: MARGIN, bottom: brandBottomMargin(config) }, head: [[`RUN ${runIndexes[0] + 1}`, ""]], body: courseBody(config.runs?.[runIndexes[0]]) });
        return doc.lastAutoTable.finalY + 4;
    }
    autoTable(doc, { ...plain, startY: y, margin: half.left, head: [[`RUN ${runIndexes[0] + 1}`, ""]], body: courseBody(config.runs?.[runIndexes[0]]) });
    const y2 = doc.lastAutoTable.finalY;
    autoTable(doc, { ...plain, startY: y, margin: half.right, head: [[`RUN ${runIndexes[1] + 1}`, ""]], body: courseBody(config.runs?.[runIndexes[1]]) });
    return Math.max(y2, doc.lastAutoTable.finalY) + 4;
}

// ---------- colonnes communes ------------------------------------------
// Nom / année acceptés sous les deux formes (live : name / yearBirth ;
// liste de départ et participants : lastname firstname / birthyear).
const nameOf = (r) => r.name || `${r.lastname || ""} ${r.firstname || ""}`.trim();
const yearOf = (r) => r.yearBirth ?? r.birthyear ?? "";

// Complète les lignes du live (classement, combiné, non classés) avec les
// données des participants, par dossard : nom, année, code, club, nation,
// genre, catégorie… — 07.10.2026. Le live ne les a pas toujours (manche
// sans liste de départ, non classés, classement combiné), d'où des colonnes
// vides dans les PDF partagés.
export function enrichLive(live, athletes) {
    if (!live) return live;
    const byBib = new Map((athletes || []).filter((a) => a.bib !== "" && a.bib != null).map((a) => [String(a.bib), a]));
    const fill = (r) => {
        const a = byBib.get(String(r.bib));
        if (!a) return r;
        return {
            ...r,
            name: r.name || `${a.lastname || ""} ${a.firstname || ""}`.trim(),
            // Nom et prénom séparés (XML FIS) : « ERIKSEN » / « Goril Strom ».
            lastname: r.lastname || a.lastname,
            firstname: r.firstname || a.firstname,
            yearBirth: r.yearBirth ?? a.birthyear,
            fiscode: r.fiscode || a.fiscode,
            club: r.club || a.club,
            nation: r.nation || a.nation,
            gender: r.gender || a.gender,
            category: r.category || a.category,
            association: r.association || a.association,
        };
    };
    return {
        ...live,
        ranking: (live.ranking || []).map(fill),
        notClassified: (live.notClassified || []).map(fill),
        combinedRanking: live.combinedRanking ? live.combinedRanking.map(fill) : live.combinedRanking,
    };
}

function identityCols(config) {
    const pop = config.raceType === "populaire";
    return {
        head: pop ? ["Bib", "Name", "YB", "Club", "Cat."] : ["Bib", "FIS Code", "Name", "YB", "Club", "NSA"],
        row: (r) => (pop
            ? [r.bib ?? "", nameOf(r), yearOf(r), r.club ?? "", r.category ?? ""]
            : [r.bib ?? "", r.fiscode ?? "", nameOf(r), yearOf(r), r.club ?? "", r.nation ?? ""]),
    };
}

// Groupes d'un PDF (07.10.2026) :
//   - « Tri impression » de la course défini (config.printSort, sections
//     par critères, voir utils/printSort.js) -> ces sections, saut de page
//     seulement si l'option « une page par section » est cochée ;
//   - sinon, par catégorie dans l'ordre défini (config.categories), une
//     page par catégorie (comportement d'origine).
// Le rang affiché est recalculé dans chaque groupe.
function groupRows(rows, grouped, config) {
    if (!grouped) return [{ category: null, rows }];
    if (toSections(config.printSort, config.categories).length) {
        return groupBySections(rows, config.printSort, config.categories).map((g) => ({
            category: g.category,
            rows: g.entries,
            pageBreak: !!config.printSort.pageBreak,
        }));
    }
    return groupByCategoryOrdered(rows, config.categories, "Without category").map((g) => ({ category: g.category, rows: g.entries, pageBreak: true }));
}

// Sauvegarde (téléchargement) ou retour d'un File, pour le partage.
function outputPdf(doc, filename, save) {
    if (save) doc.save(filename);
    return new File([doc.output("blob")], filename, { type: "application/pdf" });
}

// Données "épreuve" à partir du live : en multi-manches, le classement
// combiné (temps total, temps par manche) complété par l'identité des
// coureurs (club, code, année…) prise dans les classements de manche.
export function toEventLive(live, identityLives = []) {
    if (!live || (live.runCount || 1) <= 1 || !live.combinedRanking) return live;
    const ident = new Map();
    [live, ...identityLives].forEach((l) => (l?.ranking || []).concat(l?.notClassified || []).forEach((r) => {
        if (!ident.has(String(r.bib))) ident.set(String(r.bib), r);
    }));
    const ranking = live.combinedRanking.map((c) => ({
        ...(ident.get(String(c.bib)) || {}),
        ...c,
        elapsedMs: c.totalMs,
    }));
    return { ...live, ranking };
}

function sectionTitle(doc, y, text) {
    if (y > doc.internal.pageSize.getHeight() - 30) { doc.addPage(); y = 20; }
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text(text, MARGIN, y);
    doc.setFont(undefined, "normal");
    return y + 2;
}

// Titre de catégorie / section : espace supplémentaire avant (sauf en
// haut de page) pour bien séparer les groupes — 08.10.2026.
function groupTitle(doc, y, text, gi, pageBreak) {
    if (gi > 0 && pageBreak) {
        doc.addPage();
        y = 20;
    } else if (gi > 0) {
        y += 6;
    }
    if (y > doc.internal.pageSize.getHeight() - 40) { doc.addPage(); y = 20; }
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(text, MARGIN, y);
    doc.setFont(undefined, "normal");
    return y + 2.5;
}

function notClassified(doc, y, config, label, entries, extra) {
    if (!entries?.length) return y;
    y = sectionTitle(doc, y, `${label} (${entries.length})`);
    const id = identityCols(config);
    autoTable(doc, {
        startY: y, margin: { left: MARGIN, right: MARGIN, bottom: brandBottomMargin(config) }, styles: { fontSize: 8 }, headStyles: SUBHEAD_STYLE,
        head: [[...id.head, ...(extra?.head || [])]],
        body: entries.map((n) => [...id.row(n), ...(extra?.row ? extra.row(n) : [])]),
    });
    return doc.lastAutoTable.finalY + 6;
}

function notClassifiedBlocks(doc, y, config, { run1NotClassified, lastNotClassified, isMultiRun }) {
    const dsq = { head: ["Gate"], row: (n) => [n.gateNumber || ""] };
    const first = run1NotClassified || lastNotClassified || [];
    const last = lastNotClassified || [];
    y = notClassified(doc, y, config, "Did not start (DNS)", first.filter((n) => n.status === "DNS"));
    y = notClassified(doc, y, config, isMultiRun ? "Did not finish run 1 (DNF)" : "Did not finish (DNF)", first.filter((n) => n.status === "DNF"));
    if (isMultiRun) {
        y = notClassified(doc, y, config, "Disqualified run 1 (DSQ)", first.filter((n) => n.status === "DSQ" || n.status === "DSQP"), dsq);
        y = notClassified(doc, y, config, "Did not finish run 2 (DNF)", last.filter((n) => n.status === "DNF"));
        y = notClassified(doc, y, config, "Disqualified run 2 (DSQ)", last.filter((n) => n.status === "DSQ" || n.status === "DSQP"), dsq);
    } else {
        y = notClassified(doc, y, config, "Disqualified (DSQ)", last.filter((n) => n.status === "DSQ" || n.status === "DSQP"), dsq);
    }
    return y;
}

const fileBase = (config, eventKey) => (config.codex ? displayCodex(config.codex) : eventKey);

// ---------- 1. Liste de départ -----------------------------------------
export function startlistPdf({ config, eventKey, run, startlist, groupByCategory, includeSheet, save = true }) {
    const doc = new jsPDF();
    let y = header(doc, config, eventKey, `Start List — Run ${run}`);
    if (includeSheet) y = raceSheet(doc, y, config, [run - 1]);

    const id = identityCols(config);
    // Les entrées de liste de départ ont lastname/firstname/birthyear (pas
    // name/yearBirth comme le live) : normalisées pour les colonnes.
    const rows = (startlist || []).map((a) => ({
        ...a,
        name: a.name || `${a.lastname || ""} ${a.firstname || ""}`.trim(),
        yearBirth: a.yearBirth ?? a.birthyear,
    }));
    const sorted = [...rows].sort(
        (a, b) => (Number(a.order ?? a.startOrder ?? a.bib) || 0) - (Number(b.order ?? b.startOrder ?? b.bib) || 0)
    );
    groupRows(sorted, groupByCategory, config).forEach((g, gi) => {
        if (g.category) {
            y = groupTitle(doc, y, g.category, gi, g.pageBreak);
        }
        autoTable(doc, {
            startY: y, margin: { left: MARGIN, right: MARGIN, bottom: brandBottomMargin(config) }, ...listTableOpts(["Order", ...id.head]),
            head: [["Order", ...id.head]],
            body: g.rows.map((r, i) => [r.order ?? r.startOrder ?? i + 1, ...id.row(r)]),
        });
        y = doc.lastAutoTable.finalY + 6;
    });
    doc.setFontSize(8);
    doc.text(`Nombre de coureurs : ${sorted.length}`, MARGIN, y);

    finalize(doc, config);
    return outputPdf(doc, `${fileBase(config, eventKey)}-depart-manche${run}.pdf`, save);
}

// ---------- 2. Résultats de manche (jamais de points) -------------------
export function runResultsPdf({ config, eventKey, run, live, groupByCategory, includeSheet, save = true }) {
    const doc = new jsPDF();
    let y = header(doc, config, eventKey, `Results — Run ${run}`);
    if (includeSheet) y = raceSheet(doc, y, config, [run - 1]);

    const id = identityCols(config);
    groupRows(live.ranking || [], groupByCategory, config).forEach((g, gi) => {
        if (g.category) {
            y = groupTitle(doc, y, g.category, gi, g.pageBreak);
        }
        const best = g.rows[0]?.elapsedMs;
        const tm = isTelemark(config);
        const runHead = tm ? ["Rank", ...id.head, "Ski Time", "Penalty", "Time", "Gap"] : ["Rank", ...id.head, "Time", "Diff."];
        autoTable(doc, {
            startY: y, margin: { left: MARGIN, right: MARGIN, bottom: brandBottomMargin(config) }, ...listTableOpts(runHead),
            head: [runHead],
            body: g.rows.map((r, i) => {
                const gap = i === 0 ? "" : `+${fmtTime(r.elapsedMs - best)}`;
                if (!tm) return [g.category ? i + 1 : r.rank, ...id.row(r), fmtTime(r.elapsedMs), gap];
                return [
                    g.category ? i + 1 : r.rank, ...id.row(r),
                    fmtTime(r.skiTimeMs ?? r.elapsedMs), penText(r.stylePenalty, r.jumpPenalty), fmtTime(r.elapsedMs), gap,
                ];
            }),
        });
        y = doc.lastAutoTable.finalY + 6;
    });
    notClassifiedBlocks(doc, y, config, { lastNotClassified: live.notClassified, isMultiRun: false });

    finalize(doc, config);
    return outputPdf(doc, `${fileBase(config, eventKey)}-resultats-manche${run}.pdf`, save);
}

// ---------- Télémark : pénalités dans les listes de résultats ------------
// (03.11.2026) Comme les listes officielles télémark : temps de ski,
// pénalités « style+saut » (ex. 1+0), temps avec pénalités, écart.
const isTelemark = (config) => String(config.sector || "").toUpperCase() === "TM";
const fmtSec = (v) => {
    const n = Number(v) || 0;
    return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
};
const penText = (style, jump) => `${fmtSec(style)}+${fmtSec(jump)}`;

// Réponse de /api/timing/results-telemark -> format « live » des PDF :
// elapsedMs = temps avec pénalités ; runs[n] = { elapsedMs (avec pén.),
// skiMs, style, jump } ; skiTimeMs / stylePenalty / jumpPenalty (manche seule).
export function telemarkToLive(tm) {
    if (!tm) return tm;
    const ranking = (tm.ranking || [])
        .filter((r) => r.totalTimeMs != null)
        .map((r) => {
            const runs = {};
            Object.entries(r.runTotals || {}).forEach(([n, t]) => {
                if (t?.totalMs != null) runs[n] = { elapsedMs: t.totalMs, skiMs: t.skiMs, style: t.style ?? 0, jump: t.jump ?? 0 };
            });
            return { ...r, runs: Object.keys(runs).length ? runs : r.runs, elapsedMs: r.totalTimeMs };
        });
    return { ...tm, ranking, telemark: true };
}

// Classement final télémark à plusieurs manches, construit manche par
// manche (03.11.2026) : chaque manche lue séparément (view=run), puis
// total = somme des manches (ou meilleure manche). Ne dépend pas du
// classement combiné du serveur. getJson(url) -> objet ou null.
export async function telemarkEventLive(getJson, key, runCount, combineMethod = "sum") {
    const byBib = new Map();
    for (let n = 1; n <= runCount; n++) {
        const tm = await getJson(`/api/timing/results-telemark/${key}?run=${n}&view=run`);
        (tm?.ranking || []).forEach((r) => {
            if (r.totalTimeMs == null) return;
            const e = byBib.get(String(r.bib)) || { ...r, runs: {} };
            e.runs[n] = { elapsedMs: r.totalTimeMs, skiMs: r.skiTimeMs ?? r.elapsedMs, style: r.stylePenalty ?? 0, jump: r.jumpPenalty ?? 0 };
            byBib.set(String(r.bib), e);
        });
    }
    const cs = (ms) => Math.floor(Math.round(ms) / 10);
    const ranking = [];
    byBib.forEach((e) => {
        const times = Object.values(e.runs).map((x) => cs(x.elapsedMs) * 10);
        let total = null;
        if (combineMethod === "best") total = Math.min(...times);
        else if (Object.keys(e.runs).length === runCount) total = times.reduce((a, b) => a + b, 0);
        if (total != null) ranking.push({ ...e, elapsedMs: total, totalTimeMs: total });
    });
    ranking.sort((a, b) => a.elapsedMs - b.elapsedMs);
    ranking.forEach((r, i) => (r.rank = i > 0 && r.elapsedMs === ranking[i - 1].elapsedMs ? ranking[i - 1].rank : i + 1));
    return { runCount, ranking, telemark: true };
}

// ---------- 3. Résultats d'épreuve (points optionnels) ------------------
// pointsByBib : { [bib]: pointsFinaux } ou null (populaire / pas de calcul)
// infoLines   : lignes affichées sous la fiche (liste utilisée, pénalité...)
export function eventResultsPdf({
                                    config, eventKey, live, run1NotClassified, pointsByBib, infoLines = [], groupByCategory, includeSheet, save = true,
                                }) {
    const doc = new jsPDF();
    const ranking = live.ranking || [];
    const isMultiRun = (live.runCount || 1) > 1;
    let y = header(doc, config, eventKey, config.raceType === "populaire" ? "Ranking" : "Official Results");
    if (includeSheet) y = raceSheet(doc, y, config, isMultiRun ? [0, 1] : [0]);

    const all = [...ranking, ...(live.notClassified || []), ...(run1NotClassified || [])];
    const nations = new Set(all.map((r) => r.nation).filter(Boolean));
    const bibs = new Set(all.map((r) => String(r.bib)));
    doc.setFontSize(8);
    [...infoLines, `Number of competitors: ${bibs.size}${config.raceType !== "populaire" ? `, number of NSA: ${nations.size}` : ""}, ranked: ${ranking.length}`]
        .forEach((line) => { doc.text(line, MARGIN, y); y += 4; });
    y += 2;

    const id0 = identityCols(config);
    // Plusieurs manches : sans colonne Club (comme les listes officielles),
    // pour garder toutes les colonnes lisibles sur une ligne.
    const clubIdx = id0.head.indexOf("Club");
    const id = isMultiRun && clubIdx >= 0
        ? { head: id0.head.filter((_, i) => i !== clubIdx), row: (r) => id0.row(r).filter((_, i) => i !== clubIdx) }
        : id0;
    const withPoints = !!pointsByBib;
    const tm = isTelemark(config);
    const head = ["Rank", ...id.head];
    if (tm && !isMultiRun) head.push("Ski Time", "Penalty", "Time", "Gap");
    else {
        if (isMultiRun) head.push("Run 1", "Run 2");
        head.push(tm ? "Time" : "Total", tm ? "Gap" : "Diff.");
    }
    if (withPoints) head.push("Points");

    groupRows(ranking, groupByCategory, config).forEach((g, gi) => {
        if (g.category) {
            y = groupTitle(doc, y, g.category, gi, g.pageBreak);
        }
        const best = g.rows[0]?.elapsedMs;
        autoTable(doc, {
            startY: y, margin: { left: MARGIN, right: MARGIN, bottom: brandBottomMargin(config) }, ...listTableOpts(head),
            head: [head],
            body: g.rows.map((r, i) => {
                const row = [g.category ? i + 1 : r.rank, ...id.row(r)];
                const gap = i === 0 ? "" : `+${fmtTime(r.elapsedMs - best)}`;
                if (tm && !isMultiRun) {
                    row.push(fmtTime(r.skiTimeMs ?? r.elapsedMs), penText(r.stylePenalty, r.jumpPenalty), fmtTime(r.elapsedMs), gap);
                } else {
                    if (isMultiRun) {
                        [1, 2].forEach((n) => {
                            const x = r.runs?.[n];
                            // Télémark : temps de la manche (avec pénalités), puis ski · pénalités.
                            // Télémark : temps de la manche (gras), puis « ski (pénalité) » en petit.
                            row.push(tm && x ? { content: fmtTime(x.elapsedMs), detail: `${fmtTime(x.skiMs)} (${penText(x.style, x.jump)})` } : fmtTime(x?.elapsedMs));
                        });
                    }
                    row.push(fmtTime(r.elapsedMs), gap);
                }
                if (withPoints) row.push(fmtPts(pointsByBib[String(r.bib)]));
                return row;
            }),
        });
        y = doc.lastAutoTable.finalY + 6;
    });

    notClassifiedBlocks(doc, y, config, { run1NotClassified, lastNotClassified: live.notClassified, isMultiRun });
    finalize(doc, config);
    return outputPdf(doc, `${fileBase(config, eventKey)}-resultats.pdf`, save);
}

// ---------- 3b. Analyse d'une manche (secteurs) -------------------------
// (04.11.2026) Comme l'« Event Analysis » officielle : pour chaque coureur
// (ordre des dossards), temps de chaque SECTEUR et son rang, temps de ski,
// pénalités (télémark), temps et écart. Secteur 1 = départ → inter 1,
// secteur k = inter k-1 → inter k, dernier secteur = dernier inter →
// arrivée. Temps tronqués au 1/100.
// live : { ranking: [...], notClassified: [...] } d'UNE manche ; chaque
// entrée : inter1..inter3 (ms depuis le départ), skiTimeMs (télémark) ou
// elapsedMs, stylePenalty / jumpPenalty, totalTimeMs (télémark).
export function analysisPdf({ config, eventKey, run, live, save = true }) {
    const doc = new jsPDF();
    const tm = isTelemark(config);
    let y = header(doc, config, eventKey, `Run ${run} — Analysis`);
    const cs = (ms) => (ms == null ? null : Math.floor(Math.round(Number(ms)) / 10));
    const fcs = (c) => (c == null ? "" : fmtTime(c * 10));
    const STATUS = { DNS: "Did Not Start", DNF: "Did Not Finish", DSQ: "Disqualified", DSQP: "Disqualified (prov.)" };

    const rows = [
        ...(live.ranking || []).map((r) => ({ ...r, classified: true })),
        ...(live.notClassified || []).map((r) => ({ ...r, classified: false })),
    ];
    const interCount = Math.max(0, ...rows.map((r) => [1, 2, 3].filter((i) => r[`inter${i}`] != null).length));
    const nSect = interCount + 1;

    rows.forEach((r) => {
        const ski = cs(r.skiTimeMs ?? (tm ? null : r.elapsedMs)) ?? (r.classified ? cs(r.elapsedMs) : null);
        const marks = [0, ...[1, 2, 3].slice(0, interCount).map((i) => cs(r[`inter${i}`]))];
        r._ski = ski;
        r._sect = [];
        for (let k = 1; k <= nSect; k++) {
            const a = marks[k - 1];
            const b = k <= interCount ? marks[k] : ski;
            r._sect.push(a != null && b != null ? b - a : null);
        }
        r._time = r.classified ? cs(tm ? r.totalTimeMs ?? r.elapsedMs : r.elapsedMs) : null;
    });
    // Rang de chaque secteur, parmi les classés.
    for (let k = 0; k < nSect; k++) {
        const vals = rows.filter((r) => r.classified && r._sect[k] != null).map((r) => r._sect[k]).sort((a, b) => a - b);
        rows.forEach((r) => {
            if (r.classified && r._sect[k] != null) r[`_rk${k}`] = 1 + vals.findIndex((v) => v === r._sect[k]);
        });
    }
    const best = Math.min(...rows.filter((r) => r._time != null).map((r) => r._time));
    rows.sort((a, b) => (Number(a.bib) || 0) - (Number(b.bib) || 0));

    const head = ["Rank", "Bib", "Code", "Name", "Year", "Nation"];
    for (let k = 1; k <= nSect; k++) head.push(`Sector ${k}`);
    head.push("Ski Time");
    if (tm) head.push("Penalty");
    head.push("Time", "Gap");

    const body = rows.map((r) => {
        const row = [r.classified ? r.rank ?? "" : "", r.bib ?? "", r.fiscode ?? "", nameOf(r), yearOf(r), r.nation ?? ""];
        r._sect.forEach((v, k) => row.push(v == null ? "" : `${fcs(v)}${r[`_rk${k}`] ? ` (${r[`_rk${k}`]})` : ""}`));
        row.push(fcs(r._ski));
        if (tm) row.push(r.stylePenalty != null || r.jumpPenalty != null ? penText(r.stylePenalty, r.jumpPenalty) : "");
        row.push(r.classified ? fcs(r._time) : STATUS[r.status] || r.status || "");
        row.push(r.classified && r._time > best ? fcs(r._time - best) : "");
        return row;
    });

    const opts = listTableOpts(head);
    const nameIdx = head.indexOf("Name");
    head.forEach((h, i) => {
        if (/^Sector/.test(h)) opts.columnStyles[i] = { cellWidth: tm ? 18 : 20, halign: "right" };
    });
    // Largeurs (mm) serrées : le nom garde la place restante.
    const fixed = { Rank: 9, Bib: 9, Code: 14, Year: 9, Nation: 11, "Ski Time": 13, Penalty: 12, Time: 18, Gap: 11 };
    head.forEach((h, i) => {
        if (fixed[h]) opts.columnStyles[i] = { ...(opts.columnStyles[i] || {}), cellWidth: fixed[h] };
        if (/^Sector/.test(h)) opts.columnStyles[i] = { cellWidth: nSect > 3 ? 14 : 16.5, halign: "right" };
    });
    opts.columnStyles[nameIdx] = { fontStyle: "bold", cellWidth: "auto" };
    opts.styles = { ...opts.styles, fontSize: 7.4 };
    opts.headStyles = { ...opts.headStyles, fontSize: 6.6, overflow: "visible" };
    autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN, bottom: brandBottomMargin(config) },
        ...opts,
        head: [head],
        body,
    });
    finalize(doc, config);
    return outputPdf(doc, `${fileBase(config, eventKey)}-analyse-manche${run}.pdf`, save);
}

// ---------- 4. Détail du calcul de pénalité ------------------------------
// tables : [{ label, head: [...], body: [[...]], foot: [[...]],
//            struckRows: Set<index>, struckColumns?: [index] }]
// Modifié le 01.10.2026 (lisibilité à l'impression) : tout le texte en
// NOIR (plus de gris clair), et les points NON retenus dans le calcul sont
// BARRÉS d'un trait (colonnes struckColumns, toutes par défaut) au lieu
// d'être simplement grisés.
export function penaltyPdf({ config, eventKey, title, infoLines = [], tables }) {
    const doc = new jsPDF();
    let y = header(doc, config, eventKey, title);
    doc.setFontSize(9);
    doc.setTextColor(0);
    infoLines.forEach((l) => { doc.text(l, MARGIN, y); y += 4.5; });
    y += 2;
    tables.forEach((t) => {
        y = sectionTitle(doc, y, t.label);
        const isStruck = (data) =>
            data.section === "body" &&
            t.struckRows?.has(data.row.index) &&
            (!t.struckColumns || t.struckColumns.includes(data.column.index));
        autoTable(doc, {
            startY: y,
            margin: { left: MARGIN, right: MARGIN, bottom: brandBottomMargin(config) },
            styles: { fontSize: 9, textColor: 0, lineColor: 120, lineWidth: 0.1 },
            headStyles: HEAD_STYLE,
            footStyles: { ...SUBHEAD_STYLE, fontStyle: "bold" },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            head: [t.head],
            body: t.body,
            foot: t.foot,
            didDrawCell: (data) => {
                if (!isStruck(data)) return;
                const text = (data.cell.text || []).join(" ").trim();
                if (!text) return;
                doc.setFontSize(data.cell.styles.fontSize);
                const w = doc.getTextWidth(text);
                const padL = data.cell.padding("left");
                const padR = data.cell.padding("right");
                const x =
                    data.cell.styles.halign === "right"
                        ? data.cell.x + data.cell.width - padR - w
                        : data.cell.styles.halign === "center"
                            ? data.cell.x + (data.cell.width - w) / 2
                            : data.cell.x + padL;
                const yMid = data.cell.y + data.cell.height / 2;
                doc.setDrawColor(0);
                doc.setLineWidth(0.35);
                doc.line(x - 0.5, yMid, x + w + 0.5, yMid);
            },
        });
        y = doc.lastAutoTable.finalY + 8;
    });
    finalize(doc, config);
    doc.save(`${fileBase(config, eventKey)}-penalite.pdf`);
}